import { Grid, GridColumn, GridRow, Input } from '@bp/ui-components';
import dayjs from 'dayjs';
import { Form, Formik, FormikHelpers } from 'formik';
import { t } from 'i18next';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import {
  HolidayInput,
  useCreateHolidaysMutation,
  useUpdateHolidaysMutation,
} from '../../../types/planung-graphql-client-defs';
import { markEventJobStartedForHolidays } from '../../../utils/eventsApi';
import { dateTimeToUTCDateString } from '../../../utils/helper';
import { ModalBottomButtons } from '../../ModalBottomButtons/ModalBottomButtons';
import { SchoolYearType } from '../../SchoolYear/graphql/types';
import { HolidayFormValues, HolidayType } from '../graphql';
import { DatePickerHolidaysField } from './DatePickerHolidaysField';
import { holidaysSchema } from './validation/schema';

type HolidaysFormProps = {
  holiday: HolidayType | null;
  schoolYear?: SchoolYearType | null;
  handleClose: () => void;
};

export const HolidaysForm = ({ holiday, handleClose }: HolidaysFormProps) => {
  const initialValue: HolidayFormValues = {
    uuid: holiday?.uuid ?? '',
    name: holiday?.name ?? '',
    start: holiday?.start ? dayjs(holiday?.start).toDate() : dayjs().toDate(),
    end: holiday?.end ? dayjs(holiday?.end).toDate() : dayjs().add(1, 'days').toDate(),
  };
  const context = useMemorizedCacheTag('SCHOOL_YEAR');

  const [, update] = useUpdateHolidaysMutation();
  const [, create] = useCreateHolidaysMutation();

  // TODO: update/create needs testing
  const handleSubmit = async (values: HolidayFormValues, formHelpers: FormikHelpers<HolidayFormValues>) => {
    // Ferien ändern stößt im Backend den Abgleich der Termine an; läuft schon einer, ginge er verloren.

    const input: HolidayInput = {
      name: values.name,
      start: dateTimeToUTCDateString(values.start),
      end: dateTimeToUTCDateString(values.end),
    };

    const result = holiday?.uuid
      ? await update({ uuid: holiday.uuid, input }, context)
      : await create({ input }, context);
    if (!result.error) markEventJobStartedForHolidays(holiday, input);
    formHelpers.resetForm();
    handleClose();
  };

  return (
    <>
      <Formik initialValues={initialValue} onSubmit={handleSubmit} validationSchema={holidaysSchema}>
        {({ resetForm, values, touched, handleChange, errors, isSubmitting, dirty, isValidating }) => {
          return (
            <Form>
              <Grid useFormGap>
                <GridRow spacingBottom='s'>
                  <Input
                    label={t('common.name')}
                    name='name'
                    onChange={handleChange}
                    value={values.name}
                    {...(errors.name &&
                      touched.name && {
                        error: errors.name,
                      })}
                  />
                </GridRow>
                <GridRow spacingTop='s'>
                  <GridColumn width={6}>
                    <DatePickerHolidaysField label={t('holidays.start')} fieldName='start' updateEnd={!holiday} />
                  </GridColumn>
                  <GridColumn width={6}>
                    <DatePickerHolidaysField label={t('holidays.end')} fieldName='end' updateEnd={!holiday} />
                  </GridColumn>
                </GridRow>
              </Grid>

              <ModalBottomButtons
                closeButton={{
                  text: t('common.cancelChanges'),
                  callback: () => {
                    resetForm();
                    handleClose();
                  },
                }}
                submitButton={{
                  disabled: isSubmitting || !dirty || isValidating,
                }}
              />
            </Form>
          );
        }}
      </Formik>
    </>
  );
};
